// Per-device passkey UX state, kept in localStorage. Passkeys are inherently
// per-device, so a device-scoped store is the right home for "does this device
// have a passkey" + "how pushy should the setup nudge be". None of this is
// security state — the server is always the source of truth; these flags only
// tune when we auto-fire the Face ID prompt and when we offer setup.

const K_HAS_PASSKEY = "mp_pk_device";       // "1" once a passkey is enrolled/used here
const K_LOGIN_COUNT = "mp_login_count";     // successful logins on this device
const K_SNOOZE_UNTIL = "mp_pk_offer_until"; // ms epoch; don't offer before this
const K_DECLINES = "mp_pk_offer_declines";  // times the user dismissed the offer

const now = () => new Date().getTime();
const getNum = (k: string): number => {
  try { return parseInt(localStorage.getItem(k) || "0", 10) || 0; } catch { return 0; }
};
const setNum = (k: string, v: number) => { try { localStorage.setItem(k, String(v)); } catch {} };

// A passkey is known to exist on this device (auto-offer Face ID at login).
export const deviceHasPasskey = (): boolean => {
  try { return localStorage.getItem(K_HAS_PASSKEY) === "1"; } catch { return false; }
};
export const markDeviceHasPasskey = () => { try { localStorage.setItem(K_HAS_PASSKEY, "1"); } catch {} };
export const clearDeviceHasPasskey = () => { try { localStorage.removeItem(K_HAS_PASSKEY); } catch {} };

// Count a successful login on this device (drives "offer around the 2nd login").
export const recordLogin = (): number => { const n = getNum(K_LOGIN_COUNT) + 1; setNum(K_LOGIN_COUNT, n); return n; };
export const loginCount = (): number => getNum(K_LOGIN_COUNT);

// Snooze / decline handling for the setup nudge.
export const snoozeOffer = (days: number) => setNum(K_SNOOZE_UNTIL, now() + days * 86400_000);
export const recordDecline = (): number => {
  const n = getNum(K_DECLINES) + 1;
  setNum(K_DECLINES, n);
  // Back off harder each time: 14 days, then 60, then effectively "stop asking".
  snoozeOffer(n >= 3 ? 3650 : n === 2 ? 60 : 14);
  return n;
};

// One-shot "the user asked for Face ID but had none set up" handoff. Set on the
// login screen when a passwordless attempt finds no passkey on this device;
// consumed once, right after login, to drop the user straight into passkey
// setup. sessionStorage so it never leaks across browser sessions.
const K_SETUP_INTENT = "mp_pk_setup_intent";
export const setPasskeySetupIntent = () => { try { sessionStorage.setItem(K_SETUP_INTENT, "1"); } catch {} };
export const takePasskeySetupIntent = (): boolean => {
  try {
    const v = sessionStorage.getItem(K_SETUP_INTENT) === "1";
    if (v) sessionStorage.removeItem(K_SETUP_INTENT);
    return v;
  } catch { return false; }
};

// Whether THIS device can actually complete a platform-attachment passkey
// ceremony (Face ID / Touch ID / Windows Hello / Android biometric) — not
// just whether the browser implements the WebAuthn API. Registration always
// requests authenticatorAttachment: "platform" (backend passkey.py
// build_registration_options — deliberately, so passkeys can't fall back to
// a roaming security key), so a device with the API present but no platform
// authenticator configured (Windows Hello never set up, no biometric
// hardware) makes navigator.credentials.create() silently fail to produce
// any prompt at all — no error, no biometric dialog, just a dead button.
// Checking this first lets callers avoid offering "Create passkey" on a
// device that can never satisfy it. Memoized: the answer doesn't change
// mid-session.
let _platformAuthCache: Promise<boolean> | null = null;
export const platformAuthenticatorAvailable = (): Promise<boolean> => {
  if (_platformAuthCache) return _platformAuthCache;
  _platformAuthCache = (async () => {
    try {
      const PK = (window as any).PublicKeyCredential;
      if (!PK || typeof PK.isUserVerifyingPlatformAuthenticatorAvailable !== "function") return false;
      return await PK.isUserVerifyingPlatformAuthenticatorAvailable();
    } catch {
      return false;
    }
  })();
  return _platformAuthCache;
};

// Should we surface the gentle "set up Face ID login" nudge right now?
// Gate: browser supports it, no passkey here yet, at least the 2nd login, and
// not currently snoozed. Callers still confirm server-side that no passkey is
// enrolled before showing anything.
export const shouldOfferPasskeySetup = (): boolean => {
  if (deviceHasPasskey()) return false;
  if (loginCount() < 2) return false;
  const until = getNum(K_SNOOZE_UNTIL);
  if (until && now() < until) return false;
  return true;
};
